import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useApi } from '../../../../hooks/useApi';
import { saveSeoTransfers } from '../../../../components/OzonSeoUniqualization';
import './OzonPrecisionEdits.css';
import { matchesSearch } from '../../../../utils/search';

const PAGE_SIZE = 50;

export function parsePrecisionKeywords(value) {
    return [...new Set(
        String(value || '')
            .split(/\r?\n/)
            .map((item) => item.trim().replace(/\s+/g, ' '))
            .filter((item) => item.replace(/\*/g, '').trim())
    )];
}

function escapeRegExp(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function parseKeywordDirective(value) {
    const keyword = String(value || '').trim();
    if (keyword.startsWith('***/')) return { phrase: keyword.slice(4).trim(), direction: 'before_all_keep' };
    if (keyword.endsWith('/***')) return { phrase: keyword.slice(0, -4).trim(), direction: 'after_all_keep' };
    if (keyword.startsWith('**/')) return { phrase: keyword.slice(3).trim(), direction: 'before_punctuation_keep' };
    if (keyword.endsWith('/**')) return { phrase: keyword.slice(0, -3).trim(), direction: 'after_punctuation_keep' };
    if (keyword.startsWith('***')) return { phrase: keyword.slice(3).trim(), direction: 'before_all' };
    if (keyword.endsWith('***')) return { phrase: keyword.slice(0, -3).trim(), direction: 'after_all' };
    if (keyword.startsWith('**')) return { phrase: keyword.slice(2).trim(), direction: 'before_punctuation' };
    if (keyword.endsWith('**')) return { phrase: keyword.slice(0, -2).trim(), direction: 'after_punctuation' };
    return { phrase: keyword, direction: null };
}

function phrasePattern(phrase, global = false) {
    const directive = parseKeywordDirective(phrase);
    const body = directive.phrase
        .split('*')
        .map((part) => escapeRegExp(part).replace(/\s+/g, '\\s+'))
        .join('[\\p{L}\\p{N}-]*');
    return new RegExp(`(^|[^\\p{L}\\p{N}])(${body})(?=$|[^\\p{L}\\p{N}])`, global ? 'giu' : 'iu');
}

function applyDirectionalEdit(text, keyword, operation, replacement) {
    const { direction } = parseKeywordDirective(keyword);
    if (!direction) return null;
    const matches = [...text.matchAll(phrasePattern(keyword, true))].map((match) => ({
        start: match.index + match[1].length,
        end: match.index + match[1].length + match[2].length,
    }));
    if (!matches.length) return text;
    const value = operation === 'replace' ? replacement.trim() : '';
    if (direction === 'after_all') {
        const match = matches[0];
        return `${text.slice(0, match.start)}${value}`;
    }
    if (direction === 'after_all_keep') {
        const match = matches[0];
        return `${text.slice(0, match.end)}${value ? ` ${value}` : ''}`;
    }
    if (direction === 'before_all') {
        const match = matches[0];
        return `${value}${text.slice(match.end)}`;
    }
    if (direction === 'before_all_keep') {
        const match = matches[0];
        return `${value ? `${value} ` : ''}${text.slice(match.start)}`;
    }
    return [...matches].reverse().reduce((result, match) => {
        if (direction === 'after_punctuation' || direction === 'after_punctuation_keep') {
            const relativeEnd = result.slice(match.end).search(/[.,;:!?]/u);
            const end = relativeEnd < 0 ? result.length : match.end + relativeEnd;
            const start = direction === 'after_punctuation_keep' ? match.end : match.start;
            return `${result.slice(0, start)}${value ? `${start === match.end ? ' ' : ''}${value}` : ''}${result.slice(end)}`;
        }
        const prefix = result.slice(0, match.start);
        const punctuation = Math.max(...['.', ',', ';', ':', '!', '?'].map((mark) => prefix.lastIndexOf(mark)));
        const start = punctuation < 0 ? 0 : punctuation + 1;
        const end = direction === 'before_punctuation_keep' ? match.start : match.end;
        const inserted = end === match.start ? (value ? `${value} ` : ' ') : value;
        return `${result.slice(0, start)}${inserted}${result.slice(end)}`;
    }, text);
}

export function containsDirectPhrase(text, phrase) {
    if (!text || !phrase) return false;
    return phrasePattern(phrase).test(text);
}

export function countDirectPhrase(text, phrase) {
    if (!text || !phrase) return 0;
    return [...String(text).matchAll(phrasePattern(phrase, true))].length;
}

export function getRelevantExcerpts(text, keywords, radius = 80) {
    const source = String(text || '');
    const occurrences = [];
    keywords.forEach((keyword) => {
        for (const match of source.matchAll(phrasePattern(keyword, true))) {
            const start = match.index + match[1].length;
            const end = start + match[2].length;
            if (!occurrences.some((item) => item.start === start && item.end === end)) occurrences.push({ start, end });
        }
    });
    return occurrences.sort((a, b) => a.start - b.start).map(({ start, end }) => {
        const excerptStart = Math.max(0, start - radius);
        const excerptEnd = Math.min(source.length, end + radius);
        return {
            before: source.slice(excerptStart, start), match: source.slice(start, end), after: source.slice(end, excerptEnd),
            leading: excerptStart > 0, trailing: excerptEnd < source.length,
        };
    });
}

export function getRelevantExcerpt(text, keywords, radius = 145) {
    const source = String(text || '');
    let best = null;
    keywords.forEach((keyword) => {
        const match = phrasePattern(keyword).exec(source);
        if (!match) return;
        const start = match.index + match[1].length;
        const end = start + match[2].length;
        if (!best || start < best.start) best = { start, end };
    });
    if (!best) return { before: source.slice(0, radius * 2), match: '', after: '', leading: false, trailing: source.length > radius * 2 };
    const excerptStart = Math.max(0, best.start - radius);
    const excerptEnd = Math.min(source.length, best.end + radius);
    return {
        before: source.slice(excerptStart, best.start),
        match: source.slice(best.start, best.end),
        after: source.slice(best.end, excerptEnd),
        leading: excerptStart > 0,
        trailing: excerptEnd < source.length,
    };
}

function RelevantExcerpt({ text, keywords }) {
    const excerpts = getRelevantExcerpts(text, keywords);
    if (!excerpts.length) return <div className="precision-excerpt">Совпадений нет</div>;
    return <div className="precision-excerpts">{excerpts.slice(0, 8).map((excerpt, index) => <div className="precision-excerpt" key={`${excerpt.match}-${index}`}>
        <span className="precision-occurrence">{index + 1}</span>{excerpt.leading && '…'}{excerpt.before}<mark>{excerpt.match}</mark>{excerpt.after}{excerpt.trailing && '…'}
    </div>)}{excerpts.length > 8 && <small>Ещё совпадений: {excerpts.length - 8}</small>}</div>;
}

export function insertPrecisionText(text, addition, position = 'end') {
    const source = String(text || '').trim();
    const value = String(addition || '').trim();
    if (!value || source.toLocaleLowerCase('ru').includes(value.toLocaleLowerCase('ru'))) return source;
    if (!source) return value;
    if (position === 'start') return `${value} ${source}`;
    if (position === 'end') return `${source}${/[.!?;:]$/.test(source) ? ' ' : '. '}${value}`;

    const center = source.length / 2;
    const candidates = (pattern) => [...source.matchAll(pattern)].map((match) => match.index + match[0].length);
    let points = candidates(/[.!?](?=\s|<br\s*\/?\s*>|$)|<br\s*\/?\s*>/giu);
    if (!points.length) points = candidates(/[;,:](?=\s|$)/gu);
    if (!points.length) points = candidates(/\s+/gu);
    if (!points.length) return `${source} ${value}`;
    const insertion = points.reduce((best, point) => Math.abs(point - center) < Math.abs(best - center) ? point : best, points[0]);
    const left = source.slice(0, insertion).trimEnd();
    const right = source.slice(insertion).trimStart();
    return `${left} ${value} ${right}`.trim();
}

export function applyPrecisionEdit(text, keywords, operation, replacement = '', addition = '', addPosition = 'end') {
    const source = String(text || '');
    if (operation === 'remove' || operation === 'replace') {
        return keywords.reduce((result, keyword) => (
            applyDirectionalEdit(result, keyword, operation, replacement) ?? result.replace(phrasePattern(keyword, true), (match, prefix) => (
                `${prefix}${operation === 'replace' ? replacement.trim() : ''}`
            ))
        ), source)
            .replace(/[ \t]{2,}/g, ' ')
            .replace(/\s+([,.;:!?])/g, '$1')
            .replace(/([;,:])\s*([;,:])/g, '$1')
            .replace(/[,;:]\s*([.!?])/g, '$1')
            .replace(/([.!?])(?:\s*[.!?])+/g, '$1')
            .replace(/^[\s,;:.!?-]+|[\s,;:-]+$/g, '')
            .trim();
    }

    const missing = keywords.filter((keyword) => !containsDirectPhrase(source, keyword));
    const valueToAdd = addition || missing.map((keyword) => keyword.replace(/\*/g, '')).join('; ');
    return insertPrecisionText(source, valueToAdd, addPosition);
}

function OzonPrecisionEdits({ accountId, onOpenDescriptions }) {
    const { request, showStatus } = useApi();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [keywordInput, setKeywordInput] = useState('');
    const [operation, setOperation] = useState('remove');
    const [replacement, setReplacement] = useState('');
    const [addition, setAddition] = useState('');
    const [addPosition, setAddPosition] = useState('end');
    const [matchMode, setMatchMode] = useState('any');
    const [targetField, setTargetField] = useState('description');
    const [selected, setSelected] = useState(new Set());
    const [preview, setPreview] = useState(null);
    const [page, setPage] = useState(1);

    const loadProducts = useCallback(async () => {
        if (!accountId) return;
        setLoading(true);
        try {
            const data = await request(`/api/v1/ozon-desc/${accountId}/products`);
            setProducts(data.products || []);
            setSelected(new Set());
            setPreview(null);
            setPage(1);
        } catch (error) {
            showStatus('error', `Не удалось загрузить описания: ${error.message}`);
        } finally {
            setLoading(false);
        }
    }, [accountId, request, showStatus]);

    useEffect(() => { loadProducts(); }, [loadProducts]);

    const keywords = useMemo(() => parsePrecisionKeywords(keywordInput), [keywordInput]);
    const replaceKeywordError = operation === 'replace' && keywords.length !== 1;
    const addDirectiveError = operation === 'add' && keywords.some((keyword) => Boolean(parseKeywordDirective(keyword).direction));
    const filtered = useMemo(() => {
        return products.filter((product) => {
            const productMatches = matchesSearch(product, search);
            if (!productMatches || !keywords.length) return productMatches;
            const texts = targetField === 'name' ? [product.name] : targetField === 'both' ? [product.name, product.description] : [product.description];
            const hits = keywords.map((keyword) => texts.some((text) => containsDirectPhrase(text || '', keyword)));
            return matchMode === 'all' ? hits.every(Boolean) : hits.some(Boolean);
        });
    }, [products, search, keywords, matchMode, targetField]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const safePage = Math.min(page, totalPages);
    const pageRows = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
    const targetIds = selected.size ? selected : new Set(filtered.map((product) => product.offer_id));

    const toggle = (offerId) => {
        setPreview(null);
        setSelected((current) => {
            const next = new Set(current);
            next.has(offerId) ? next.delete(offerId) : next.add(offerId);
            return next;
        });
    };

    const buildPreview = () => {
        if (!keywords.length && operation !== 'add') {
            showStatus('info', 'Введите хотя бы один ключевой запрос');
            return;
        }
        if (operation === 'add' && !addition.trim()) {
            showStatus('info', 'Введите текст, который нужно добавить');
            return;
        }
        if (addDirectiveError) {
            showStatus('info', 'Операторы ** и *** доступны только для удаления и замены');
            return;
        }
        if (operation === 'replace' && !replacement.trim()) {
            showStatus('info', 'Введите текст, на который нужно заменить найденные запросы');
            return;
        }
        if (replaceKeywordError) {
            showStatus('info', 'Для замены оставьте ровно один ключевой запрос');
            return;
        }
        const rows = products
            .filter((product) => targetIds.has(product.offer_id))
            .map((product) => {
                const beforeName = product.name || '';
                const beforeDescription = product.description || '';
                return {
                    ...product,
                    beforeName,
                    beforeDescription,
                    afterName: targetField !== 'description' ? applyPrecisionEdit(beforeName, keywords, operation, replacement, addition, addPosition) : beforeName,
                    afterDescription: targetField !== 'name' ? applyPrecisionEdit(beforeDescription, keywords, operation, replacement, addition, addPosition) : beforeDescription,
                };
            })
            .filter((product) => product.beforeName !== product.afterName || product.beforeDescription !== product.afterDescription);
        setPreview(rows);
        if (!rows.length) showStatus('info', 'Изменений не найдено: проверьте ключи и выбранные товары');
    };

    const transferToDescriptions = async () => {
        if (!preview?.length) return;
        const pending = {};
        preview.forEach((product) => {
            pending[product.offer_id] = {
                originalDesc: product.beforeDescription,
                ...(product.beforeDescription !== product.afterDescription ? { seoText: product.afterDescription } : {}),
                ...(product.beforeName !== product.afterName ? { name: product.afterName } : {}),
                productName: product.beforeName,
            };
        });
        if (!(await saveSeoTransfers(request, accountId, pending))) {
            showStatus('error', 'Не удалось передать изменения в «Описания». Попробуйте ещё раз.');
            return;
        }
        showStatus('success', `Подготовлено изменений: ${preview.length}. Проверьте их в «Описаниях».`);
        onOpenDescriptions?.();
    };

    const updatePreviewText = (offerId, field, value) => {
        setPreview((current) => current?.map((product) => (
            product.offer_id === offerId ? { ...product, [field]: value } : product
        )));
    };

    return (
        <div className="precision-edits">
            <section className="precision-card precision-intro">
                <div>
                    <span className="precision-eyebrow">Работа с ошибками</span>
                    <h3>Точечные изменения в описаниях</h3>
                    <p>Найдите товары по артикулу или ключу, массово добавьте, удалите или замените запросы и передайте результат на проверку в «Описания».</p>
                </div>
                <button type="button" className="precision-button precision-button--ghost primary" onClick={loadProducts} disabled={loading}>
                    {loading ? 'Обновляем…' : 'Обновить данные'}
                </button>
            </section>

            <section className="precision-card precision-controls">
                <div className="precision-field precision-field--wide">
                    <label htmlFor="precision-keywords">Ключевые запросы для поиска товаров</label>
                    <textarea id="precision-keywords" value={keywordInput} onChange={(event) => { setKeywordInput(event.target.value); setPreview(null); setPage(1); }} placeholder={'Например:\nкрем для рук\nподарочный набор'} />
                    <small className="precision-key-guide">
                        <span>Каждый ключ вводится с новой строки. Запятая считается частью ключа.</span>
                        <span><code>ключ*</code> — любое окончание слова.</span>
                        <span><code>ключ***</code> — ключ и весь текст после него.</span>
                        <span><code>***ключ</code> — весь текст до ключа и сам ключ.</span>
                        <span><code>ключ**</code> — ключ и текст после него до ближайшего знака препинания.</span>
                        <span><code>**ключ</code> — текст от предыдущего знака препинания и сам ключ.</span>
                        <span><code>ключ/***</code> — весь текст после ключа; ключ сохраняется.</span>
                        <span><code>***/ключ</code> — весь текст до ключа; ключ сохраняется.</span>
                        <span><code>ключ/**</code> — текст после ключа до ближайшего знака препинания; ключ сохраняется.</span>
                        <span><code>**/ключ</code> — текст от предыдущего знака препинания до ключа; ключ сохраняется.</span>
                    </small>
                </div>
                <div className="precision-field">
                    <label>Действие</label>
                    <div className="precision-segmented">
                        <button type="button" className={operation === 'remove' ? 'active danger' : ''} onClick={() => { setOperation('remove'); setPreview(null); }}>Удалить</button>
                        <button type="button" className={operation === 'add' ? 'active success' : ''} onClick={() => { setOperation('add'); setPreview(null); }}>Добавить</button>
                        <button type="button" className={operation === 'replace' ? 'active replace' : ''} onClick={() => { setOperation('replace'); setPreview(null); }}>Заменить</button>
                    </div>
                    {operation === 'replace' && <div className="precision-replacement"><label htmlFor="precision-replacement">Заменить на</label><input id="precision-replacement" type="text" value={replacement} onChange={(event) => { setReplacement(event.target.value); setPreview(null); }} placeholder="Новый запрос" /></div>}
                    {operation === 'add' && <div className="precision-addition"><label htmlFor="precision-addition">Что добавить</label><textarea id="precision-addition" value={addition} onChange={(event) => { setAddition(event.target.value); setPreview(null); }} placeholder="Текст, который будет добавлен" rows="3" /><label>Куда добавить</label><div className="precision-position"><button type="button" className={addPosition === 'start' ? 'active' : ''} onClick={() => { setAddPosition('start'); setPreview(null); }}>В начало</button><button type="button" className={addPosition === 'middle' ? 'active' : ''} onClick={() => { setAddPosition('middle'); setPreview(null); }}>В середину</button><button type="button" className={addPosition === 'end' ? 'active' : ''} onClick={() => { setAddPosition('end'); setPreview(null); }}>В конец</button></div></div>}
                    {addDirectiveError && <div className="precision-validation" role="alert">Операторы ** и *** не работают в режиме «Добавить». Уберите их из ключевых запросов.</div>}
                    {replaceKeywordError && <div className="precision-validation" role="alert">Для режима «Заменить» нужен ровно один ключевой запрос. Удалите лишние строки.</div>}
                    <label className="precision-check"><input type="radio" checked={matchMode === 'any'} onChange={() => { setMatchMode('any'); setPage(1); }} /> Найден любой ключ</label>
                    <label className="precision-check"><input type="radio" checked={matchMode === 'all'} onChange={() => { setMatchMode('all'); setPage(1); }} /> Найдены все ключи</label>
                </div>
                <div className="precision-field">
                    <label htmlFor="precision-product-search">Товары</label>
                    <input id="precision-product-search" type="search" value={search} onChange={(event) => { setSearch(event.target.value); setPage(1); setPreview(null); }} placeholder="Артикул, SKU или название" />
                    <label>Где искать и изменять</label>
                    <select value={targetField} onChange={(event) => { setTargetField(event.target.value); setPage(1); setPreview(null); }}>
                        <option value="name">Название</option><option value="description">Описание</option><option value="both">Название и описание</option>
                    </select>
                    <small>{keywords.length ? 'Учитываются все вхождения ключа в выбранных полях.' : 'Без ключей показывается весь ассортимент.'}</small>
                </div>
            </section>

            <section className="precision-card precision-results">
                <div className="precision-results__header">
                    <div><strong>Найдено: {filtered.length}</strong><span> · выбрано: {selected.size || 'все найденные'}</span></div>
                    <div className="precision-actions">
                        <button type="button" className="precision-link" onClick={() => setSelected(new Set(filtered.map((item) => item.offer_id)))} disabled={!filtered.length}>Выбрать найденные</button>
                        <button type="button" className="precision-link" onClick={() => setSelected(new Set())}>Сбросить</button>
                        <button type="button" className="precision-button precision-button--preview" onClick={buildPreview} disabled={loading || !filtered.length || (operation !== 'add' && !keywords.length) || (operation === 'add' && (!addition.trim() || addDirectiveError)) || (operation === 'replace' && (!replacement.trim() || replaceKeywordError))}>Показать изменения →</button>
                    </div>
                </div>
                <div className="precision-table-wrap">
                    <table className="precision-table">
                        <thead><tr><th><input type="checkbox" checked={filtered.length > 0 && filtered.every((item) => selected.has(item.offer_id))} onChange={(event) => setSelected(event.target.checked ? new Set(filtered.map((item) => item.offer_id)) : new Set())} /></th><th>Товар</th><th>Точные вхождения</th><th>Фрагменты с ключами</th></tr></thead>
                        <tbody>
                            {pageRows.map((product) => {
                                const texts = targetField === 'name' ? [product.name] : targetField === 'both' ? [product.name, product.description] : [product.description];
                                const found = keywords.map((keyword) => ({ keyword, count: texts.reduce((sum, text) => sum + countDirectPhrase(text || '', keyword), 0) })).filter((item) => item.count);
                                return <tr key={product.offer_id} className={selected.has(product.offer_id) ? 'selected' : ''}>
                                    <td><input type="checkbox" checked={selected.has(product.offer_id)} onChange={() => toggle(product.offer_id)} /></td>
                                    <td><strong>{product.name || 'Без названия'}</strong><small>{product.offer_id}{product.sku ? ` · SKU ${product.sku}` : ''}</small></td>
                                    <td><div className="precision-chips">{found.length ? found.map((item) => <span key={item.keyword}>{item.keyword}{item.count > 1 ? ` × ${item.count}` : ''}</span>) : <em>не найдено</em>}</div></td>
                                    <td><div className="precision-field-excerpts">{targetField !== 'description' && <div><b>Название</b><RelevantExcerpt text={product.name || ''} keywords={keywords} /></div>}{targetField !== 'name' && <div><b>Описание</b><RelevantExcerpt text={product.description || ''} keywords={keywords} /></div>}</div></td>
                                </tr>;
                            })}
                            {!loading && !pageRows.length && <tr><td colSpan="4" className="precision-empty">Товары не найдены</td></tr>}
                        </tbody>
                    </table>
                </div>
                {totalPages > 1 && <div className="precision-pager"><button type="button" onClick={() => setPage((value) => Math.max(1, value - 1))} disabled={safePage === 1}>←</button><span>{safePage} из {totalPages}</span><button type="button" onClick={() => setPage((value) => Math.min(totalPages, value + 1))} disabled={safePage === totalPages}>→</button></div>}
            </section>

            {preview && <section className="precision-card precision-preview">
                <div className="precision-results__header"><div><span className="precision-eyebrow">Предпросмотр</span><strong>Будет изменено: {preview.length}</strong></div><button type="button" className="precision-button precision-button--success" onClick={transferToDescriptions} disabled={!preview.length}>Передать в «Описания» →</button></div>
                <div className="precision-preview__hint">Проверьте результат. Поле «Станет» можно отредактировать вручную перед передачей.</div>
                {preview.slice(0, 20).map((product) => <article key={product.offer_id} className="precision-diff-card"><header><strong>{product.beforeName || product.offer_id}</strong><span>{product.offer_id}</span></header>{product.beforeName !== product.afterName && <div className="precision-diff"><div><label>Название — было</label><p>{product.beforeName || 'Пусто'}</p></div><div className="after"><label htmlFor={`precision-name-${product.offer_id}`}>Название — станет · можно исправить</label><textarea id={`precision-name-${product.offer_id}`} value={product.afterName} onChange={(event) => updatePreviewText(product.offer_id, 'afterName', event.target.value)} rows="3" /></div></div>}{product.beforeDescription !== product.afterDescription && <div className="precision-diff precision-diff--description"><div><label>Описание — было</label><p>{product.beforeDescription || 'Пусто'}</p></div><div className="after"><label htmlFor={`precision-desc-${product.offer_id}`}>Описание — станет · можно исправить</label><textarea id={`precision-desc-${product.offer_id}`} value={product.afterDescription} onChange={(event) => updatePreviewText(product.offer_id, 'afterDescription', event.target.value)} rows="12" /></div></div>}</article>)}
                {preview.length > 20 && <p className="precision-more">И ещё {preview.length - 20} товаров. Все изменения будут переданы.</p>}
            </section>}
        </div>
    );
}

export default OzonPrecisionEdits;
